import { useState, useEffect, useCallback } from 'react';
import { CandidateSummary, CandidateStatusCounts, CandidateFilters } from '@recruitai/shared';
import { apiClient } from '../lib/api-client';

interface ListCandidatesResponse {
  items: CandidateSummary[];
  nextToken?: string;
  counts: CandidateStatusCounts;
}

interface UseCandidatesResult {
  candidates: CandidateSummary[];
  counts: CandidateStatusCounts | null;
  isLoading: boolean;
  error: Error | null;
  nextToken?: string;
  refetch: () => Promise<void>;
}

export function useCandidates(jobId: string, filters?: CandidateFilters): UseCandidatesResult {
  const [candidates, setCandidates] = useState<CandidateSummary[]>([]);
  const [counts, setCounts] = useState<CandidateStatusCounts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();

  const fetchCandidates = useCallback(async () => {
    if (!jobId) return;
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.minScore !== undefined) params.set('minScore', String(filters.minScore));
      if (filters?.maxScore !== undefined) params.set('maxScore', String(filters.maxScore));
      if (filters?.nextToken) params.set('nextToken', filters.nextToken);

      const qs = params.toString();
      const endpoint = `/jobs/${jobId}/candidates${qs ? `?${qs}` : ''}`;

      const response = await apiClient.get<ListCandidatesResponse>(endpoint);
      setCandidates(response.items);
      setCounts(response.counts);
      setNextToken(response.nextToken);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Falha ao carregar candidatos'));
    } finally {
      setIsLoading(false);
    }
  }, [jobId, filters?.status, filters?.minScore, filters?.maxScore, filters?.nextToken]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  return { candidates, counts, isLoading, error, nextToken, refetch: fetchCandidates };
}
